import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MapPin, TrendingUp, AlertTriangle, Shield, ArrowLeft, Bell, Map, Phone, Book, Share2, FileText } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import StoryCardGenerator from '@/components/StoryCardGenerator';
import UserReportForm from '@/components/UserReportForm';
import SafetyHeatmap from '@/components/SafetyHeatmap';
import EmergencyDirectory from '@/components/EmergencyDirectory';
import SafetyGuideDisplay from '@/components/SafetyGuideDisplay';

const API = "https://safesheatlas.com/api";

const DestinationPageNew = () => {
  const { id } = useParams();
  const [destination, setDestination] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [storyCardData, setStoryCardData] = useState(null);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchDestinationDetails();
    fetchTrendData();
    fetchStoryCardData();
  }, [id]);

  const fetchDestinationDetails = async () => {
    try {
      const response = await axios.get(`${API}/destination/${id}/details`);
      setDestination(response.data.destination);
      setMetrics(response.data.metrics);
      setIncidents(response.data.recent_incidents);
      setRecommendations(response.data.recommendations);
    } catch (error) {
      console.error('Error fetching destination details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrendData = async () => {
    try {
      const response = await axios.get(`${API}/destination/${id}/trends?days=30`);
      setTrendData(response.data);
    } catch (error) {
      console.error('Error fetching trend data:', error);
    }
  };

  const fetchStoryCardData = async () => {
    try {
      const response = await axios.get(`${API}/story-card/${id}`);
      setStoryCardData(response.data);
    } catch (error) {
      console.error('Error fetching story card data:', error);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setSubscribing(true);

    try {
      await axios.post(`${API}/subscribe`, {
        email,
        locations: [id],
      });
      toast.success('Successfully subscribed to alerts for this destination!');
      setEmail('');
    } catch (error) {
      toast.error('Subscription failed. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const getScoreClass = (score) => {
    if (score >= 80) return 'score-excellent';
    if (score >= 60) return 'score-good';
    if (score >= 40) return 'score-moderate';
    return 'score-poor';
  };

  const getRiskLevel = (score) => {
    if (score >= 80) return { text: 'Low Risk', color: 'text-green-600', bg: 'bg-green-100' };
    if (score >= 60) return { text: 'Moderate', color: 'text-teal-600', bg: 'bg-teal-100' };
    if (score >= 40) return { text: 'Elevated', color: 'text-orange-600', bg: 'bg-orange-100' };
    return { text: 'High Risk', color: 'text-red-600', bg: 'bg-red-100' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="inline-block w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!destination || !metrics) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="pt-32 text-center">
          <h1 className="text-3xl font-bold mb-4">Destination not found</h1>
          <Link to="/rankings">
            <Button className="mt-4">Back to Rankings</Button>
          </Link>
        </div>
      </div>
    );
  }

  const riskLevel = getRiskLevel(metrics.composite_wsi);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="pt-24 pb-16" data-testid="destination-page">
        <div className="max-w-7xl mx-auto px-6">
          {/* Back Button */}
          <Link to="/rankings" className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-6">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Rankings
          </Link>

          {/* Hero Section */}
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-8 fade-in" data-testid="destination-hero">
            <div className="relative h-64 md:h-96">
              <img
                src={destination.image_url}
                alt={destination.city}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <h1 className="text-4xl sm:text-5xl font-bold mb-2">{destination.city}</h1>
                <p className="text-base sm:text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  {destination.country}
                </p>
              </div>
            </div>
          </div>

          {/* Safety Score Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {/* Main Score Card */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="score-card">
                <div className={`w-32 h-32 mx-auto rounded-full ${getScoreClass(metrics.composite_wsi)} text-white flex items-center justify-center mb-6 shadow-xl`}>
                  <div>
                    <div className="text-5xl font-bold">{metrics.composite_wsi.toFixed(0)}</div>
                    <div className="text-sm opacity-90">/ 100</div>
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2">Women Safety Index</h3>
                <div className={`inline-block px-4 py-2 rounded-full ${riskLevel.bg} ${riskLevel.color} font-semibold text-sm`}>
                  {riskLevel.text}
                </div>

                {metrics.trend_7day !== 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex items-center justify-center gap-2">
                      <TrendingUp className={`w-5 h-5 ${metrics.trend_7day > 0 ? 'text-green-500' : 'text-red-500 rotate-180'}`} />
                      <span className={`text-lg font-semibold ${metrics.trend_7day > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {metrics.trend_7day > 0 ? '+' : ''}{metrics.trend_7day.toFixed(1)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">7-day change</p>
                  </div>
                )}
              </div>
            </div>

            {/* Detailed Metrics */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="metrics-breakdown">
                <h3 className="text-2xl font-bold mb-6">Safety Breakdown</h3>
                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Crime Rate (Lower is better)</span>
                      <span className="text-sm font-bold">{(100 - metrics.crime_rate).toFixed(0)}/100</span>
                    </div>
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-purple-500 to-teal-500" style={{ width: `${100 - metrics.crime_rate}%` }} />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Emergency Response</span>
                      <span className="text-sm font-bold">{metrics.emergency_score.toFixed(0)}/100</span>
                    </div>
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-purple-500 to-teal-500" style={{ width: `${metrics.emergency_score}%` }} />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Public Transport Safety</span>
                      <span className="text-sm font-bold">{metrics.transport_score.toFixed(0)}/100</span>
                    </div>
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-purple-500 to-teal-500" style={{ width: `${metrics.transport_score}%` }} />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Night Safety</span>
                      <span className="text-sm font-bold">{metrics.night_score.toFixed(0)}/100</span>
                    </div>
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-purple-500 to-teal-500" style={{ width: `${metrics.night_score}%` }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabbed Content */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6 gap-2">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <span className="hidden sm:inline">Overview</span>
              </TabsTrigger>
              <TabsTrigger value="map" className="flex items-center gap-2">
                <Map className="w-4 h-4" />
                <span className="hidden sm:inline">Heatmap</span>
              </TabsTrigger>
              <TabsTrigger value="guide" className="flex items-center gap-2">
                <Book className="w-4 h-4" />
                <span className="hidden sm:inline">Guide</span>
              </TabsTrigger>
              <TabsTrigger value="emergency" className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <span className="hidden sm:inline">Emergency</span>
              </TabsTrigger>
              <TabsTrigger value="report" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Report</span>
              </TabsTrigger>
              <TabsTrigger value="share" className="flex items-center gap-2">
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-8">
              {/* Trend Chart */}
              {trendData.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="trend-chart">
                  <h3 className="text-2xl font-bold mb-6">30-Day Safety Trend</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="date" stroke="#6B7280" style={{ fontSize: '12px' }} />
                      <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} domain={[0, 100]} />
                      <Tooltip />
                      <Line type="monotone" dataKey="composite_wsi" stroke="#8B5CF6" strokeWidth={3} dot={{ fill: '#8B5CF6', r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Recommendations */}
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Shield className="w-8 h-8 text-purple-600" />
                    <h3 className="text-2xl font-bold">Safety Recommendations</h3>
                  </div>
                  <ul className="space-y-4">
                    {recommendations.map((rec, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-2 h-2 rounded-full bg-purple-600 mt-2 flex-shrink-0" />
                        <span className="text-sm text-gray-700 leading-relaxed">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Recent Incidents */}
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <AlertTriangle className="w-8 h-8 text-orange-600" />
                    <h3 className="text-2xl font-bold">Recent Incidents</h3>
                  </div>
                  {incidents.length > 0 ? (
                    <div className="space-y-4">
                      {incidents.slice(0, 5).map((incident, index) => (
                        <div key={index} className="pb-4 border-b border-gray-200 last:border-0">
                          <p className="text-sm font-medium text-gray-800 mb-1">{incident.headline}</p>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>{incident.source}</span>
                            <span>•</span>
                            <span>{incident.date}</span>
                            <span
                              className={`px-2 py-0.5 rounded-full ${
                                incident.severity === 'high'
                                  ? 'bg-red-100 text-red-700'
                                  : incident.severity === 'medium'
                                  ? 'bg-orange-100 text-orange-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}
                            >
                              {incident.severity}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600">No recent incidents reported</p>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Safety Heatmap Tab */}
            <TabsContent value="map">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-2xl font-bold mb-6">Interactive Safety Heatmap</h3>
                <SafetyHeatmap 
                  destinationId={id} 
                  center={{ lat: destination.latitude, lng: destination.longitude }}
                />
              </div>
            </TabsContent>

            {/* Safety Guide Tab */}
            <TabsContent value="guide">
              <SafetyGuideDisplay destinationId={id} />
            </TabsContent>

            {/* Emergency Directory Tab */}
            <TabsContent value="emergency">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-2xl font-bold mb-6">Emergency Contacts & SOS Directory</h3>
                <EmergencyDirectory destinationId={id} />
              </div>
            </TabsContent>

            {/* User Report Tab */}
            <TabsContent value="report">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-2xl font-bold mb-6">Submit Safety Report</h3>
                <p className="text-gray-600 mb-6">Help other women travelers by sharing your safety experience in {destination.city}.</p>
                <UserReportForm destinationId={id} onSubmitSuccess={() => toast.success('Report submitted!')} />
              </div>
            </TabsContent>

            {/* Share Story Card Tab */}
            <TabsContent value="share">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-2xl font-bold mb-6 text-center">Share Safety Report</h3>
                {storyCardData ? (
                  <StoryCardGenerator storyData={storyCardData} />
                ) : (
                  <p className="text-center text-gray-500">Loading story card...</p>
                )}
              </div>
            </TabsContent>
          </Tabs>

          {/* Subscribe Section */}
          <div className="bg-gradient-to-br from-purple-50 to-teal-50 rounded-2xl shadow-lg p-8 text-center mt-8">
            <Bell className="w-12 h-12 mx-auto mb-4 text-purple-600" />
            <h3 className="text-2xl font-bold mb-3">Get Daily Updates</h3>
            <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">
              Receive daily safety alerts for {destination.city} directly to your inbox
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 px-6 py-3"
              />
              <Button
                type="submit"
                disabled={subscribing}
                className="px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold"
              >
                {subscribing ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DestinationPageNew;
